import moment from 'moment-timezone';
import {type OrderStatusFilter} from './constants';

export const ABSOLUTE_DATE_FILTER = 'ABSOLUTE';
export const RELATIVE_DATE_FILTER = 'RELATIVE';

export type AbsoluteDateFilter = {
  type: typeof ABSOLUTE_DATE_FILTER;
  start: Date;
  end: Date;
};

export type RelativeDateFilter = {
  type: typeof RELATIVE_DATE_FILTER;
  value: RelativeDateRange;
};

type DateFilter = AbsoluteDateFilter | RelativeDateFilter;

export type ExportOptions = {
  status: OrderStatusFilter;
  includeLineItems: boolean;
  includeAdditionalInfo: boolean;
  dateFilter: DateFilter;
};

export const RELATIVE_DATE_RANGES = {
  ALL_TIME: 'ALL_TIME',
  TODAY: 'TODAY',
  YESTERDAY: 'YESTERDAY',
  THIS_WEEK: 'THIS_WEEK',
  LAST_WEEK: 'LAST_WEEK',
  THIS_MONTH: 'THIS_MONTH',
  LAST_MONTH: 'LAST_MONTH',
  THIS_YEAR: 'THIS_YEAR',
  LAST_YEAR: 'LAST_YEAR',
} as const;

Object.freeze(RELATIVE_DATE_RANGES);

const FILENAME_TIMESTAMP_FORMAT = 'Y-MM-DD';

export type RelativeDateRange = keyof typeof RELATIVE_DATE_RANGES;

const _RELATIVE_DATE_RANGE_TO_MOMENT_PERIOD = {
  [RELATIVE_DATE_RANGES.TODAY]: 'day',
  [RELATIVE_DATE_RANGES.YESTERDAY]: 'day',
  [RELATIVE_DATE_RANGES.THIS_WEEK]: 'week',
  [RELATIVE_DATE_RANGES.LAST_WEEK]: 'week',
  [RELATIVE_DATE_RANGES.THIS_MONTH]: 'month',
  [RELATIVE_DATE_RANGES.LAST_MONTH]: 'month',
  [RELATIVE_DATE_RANGES.THIS_YEAR]: 'year',
  [RELATIVE_DATE_RANGES.LAST_YEAR]: 'year',
  [RELATIVE_DATE_RANGES.ALL_TIME]: null,
} as const;

/**
 * Combines the current time and a RelativeDateFilter
 * value to form an AbsoluteDateFilter to use in a filter clause.
 *
 * @param  {RelativeDateFilter} relativeDateFilter
 * @param  {string} timezone - standard timezone identifier, e.g. 'America/Los_Angeles'.
 *                             Defaults to UTC.
 * @param  {Date} now - Date instance to use as current time.
 *                      Defaults to current system time. Must be in UTC.
 * @return {AbsoluteDateFilter | null}
 */
export function relativeToAbsoluteDateFilter(
  relativeDateFilter: RelativeDateFilter,
  timezone: string = 'UTC',
  now: Date = new Date()
): AbsoluteDateFilter | null {
  const {value: dateRange} = relativeDateFilter;
  if (dateRange === RELATIVE_DATE_RANGES.ALL_TIME) {
    return null;
  }

  const period = _RELATIVE_DATE_RANGE_TO_MOMENT_PERIOD[dateRange];

  if (!period) {
    throw new Error(`Unhandled dateRange ${dateRange}`);
  }

  let start = moment.utc(now).clone().tz(timezone).startOf(period);

  const previousPeriodRanges = [
    RELATIVE_DATE_RANGES.YESTERDAY,
    RELATIVE_DATE_RANGES.LAST_WEEK,
    RELATIVE_DATE_RANGES.LAST_MONTH,
    RELATIVE_DATE_RANGES.LAST_YEAR,
  ];

  // @ts-expect-error - TS2345 - Argument of type '"TODAY" | "YESTERDAY" | "THIS_WEEK" | "LAST_WEEK" | "THIS_MONTH" | "LAST_MONTH" | "THIS_YEAR" | "LAST_YEAR"' is not assignable to parameter of type '"YESTERDAY" | "LAST_WEEK" | "LAST_MONTH" | "LAST_YEAR"'.
  if (previousPeriodRanges.includes(dateRange)) {
    start = start.subtract(1, period);
  }

  return {
    type: ABSOLUTE_DATE_FILTER,
    start: start.clone().utc().toDate(),
    end: start.clone().add(1, period).utc().toDate(),
  };
}

export function getCSVFilename({
  siteShortName,
  dateFilter,
  status,
  includeLineItems = false,
  includeAdditionalInfo = false,
  timezone = 'UTC',
}: {
  siteShortName: string;
  dateFilter?: AbsoluteDateFilter;
  status: OrderStatusFilter;
  includeLineItems?: boolean;
  includeAdditionalInfo?: boolean;
  timezone?: string;
}): string {
  let timestamp;
  if (dateFilter) {
    const {start, end} = dateFilter;
    const startInclusiveInTz = moment.tz(start, timezone);
    const endExclusiveInTz = moment.tz(end, timezone);

    // Moments/Dates work with 1 millisecond granularity.
    const endInclusiveInTz = endExclusiveInTz
      .clone()
      .subtract(1, 'milliseconds');

    const startFormatted = startInclusiveInTz.format(FILENAME_TIMESTAMP_FORMAT);
    const endFormatted = endInclusiveInTz.format(FILENAME_TIMESTAMP_FORMAT);

    timestamp =
      startFormatted === endFormatted
        ? startFormatted
        : [startFormatted, endFormatted].join('-');
  }

  const base = [
    siteShortName,
    'orders',
    includeLineItems ? 'line_items' : '',
    includeAdditionalInfo ? 'additional_info' : '',
    status.toLowerCase(),
    timestamp,
  ]
    .filter(Boolean)
    .join('_');

  return `${base}.csv`;
}
